import React, { useEffect, useState } from "react";
import { faRotate } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { loadContactOptionsPaginated, proxyAPI } from "../proxyAPI";
import JsonCollapse from "../Builder/Components/JsonCollapse";
import MyPagination from "../Common/MyPagination";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import { contactOption } from "./RealtimeReportingPage";
import FullScreenLoadingIndicator from "../Common/FullScreenLoadingIndicator";
import ReportingContactsList from "./ReportingContactsList";
import TicketStatusHistoryModal from "./TicketStatusHistoryModal";

const formatDateTime = (dateTime) => {
  if (!dateTime) return "-";

  const date = new Date(dateTime.slice(-1) === "Z" ? dateTime : `${dateTime}Z`);

  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
};

export default function TicketReporting() {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [topics, setTopics] = useState([]);
  const [filterTopics, setFilterTopics] = useState([]);
  const [filterContacts, setFilterContacts] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [dateLTE, setDateLTE] = useState(null);
  const [dateGTE, setDateGTE] = useState(null);
  const [closedLTE, setClosedLTE] = useState(null);
  const [closedGTE, setClosedGTE] = useState(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const getTickets = async (page = 0, perPage = 10) => {
    setLoading(true);
    const data = await proxyAPI({
      prefix: "logic/reporting",
      url: `tickets?pagination.perPage=${perPage}&pagination.page=${page}${
        filterTopics.length > 0
          ? `&filters.topics=${filterTopics.join("&filters.topics=")}`
          : ""
      }${
        filterContacts.length > 0
          ? `&filters.contact_uuids=${filterContacts.join(
              "&filters.contact_uuids="
            )}`
          : ""
      }${
        filterStatuses.length > 0
          ? `&filters.statuses=${filterStatuses.join("&filters.statuses=")}`
          : ""
      }${search.length > 0 ? `&filters.search=${search}` : ""}${
        dateGTE ? `&filters.date_gte=${dateGTE}` : ""
      }${dateLTE ? `&filters.date_lte=${dateLTE}` : ""}${
        closedGTE ? `&filters.closed_gte=${closedGTE}` : ""
      }${closedLTE ? `&filters.closed_lte=${closedLTE}` : ""}`,
    });

    setTimeout(() => {
      setLoading(false);
      if (data?.data) {
        setTickets(data.data);
        setTotal(data.info.total);
      }
    }, 500);
  };

  useEffect(() => {
    (async () => {
      const topicsData = await proxyAPI({
        prefix: "logic/reporting",
        url: `topics`,
      });
      if (topicsData) {
        setTopics(topicsData.data);
      }

      const statusesData = await proxyAPI({
        prefix: "logic/reporting",
        url: "ticket-statuses",
      });
      if (statusesData?.data) {
        setStatuses(statusesData.data);
      }
    })();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => {
      clearTimeout(timeout);
    };
  }, [searchInput]);

  return (
    <div>
      <FullScreenLoadingIndicator loading={loading} />
      <div className=" ">
        <fieldset disabled={loading}>
          <div className="mb-3 bg-colored-reverse rounded-3 p-3 mx-0">
            <div className="d-flex flex-column gap-2">
              <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 d-flex flex-wrap align-items-stretch gap-3">
                <div
                  className="text-white fw-bold small text-uppercase text-nowrap me-2 d-flex align-items-center"
                  style={{ width: 130 }}
                >
                  Search
                </div>
                <div className="flex-grow-1">
                  <label htmlFor="ticket-search" className="visually-hidden">
                    Search
                  </label>
                  <Input
                    id="ticket-search"
                    placeholder="Search tickets"
                    value={[searchInput]}
                    onChange={(v) => setSearchInput(v[0])}
                  />
                </div>
              </div>
              {showAdvancedFilters && (
                <div className="d-flex flex-column gap-2">
                  <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 d-flex flex-wrap align-items-stretch gap-3">
                    <div
                      className="text-white fw-bold small text-uppercase text-nowrap me-2 d-flex align-items-center"
                      style={{ width: 130 }}
                    >
                      Topics
                    </div>
                    <div className="flex-grow-1">
                      <Input
                        id="ticket-topics"
                        inputType="select"
                        isMulti
                        placeholder="Select topics"
                        isValidNewOption={() => false}
                        value={filterTopics}
                        onChange={(v) => setFilterTopics(v)}
                        options={topics.map((t) => ({
                          value: t.uuid,
                          label: t.label,
                        }))}
                      />
                    </div>
                  </div>
                  <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 d-flex flex-wrap align-items-stretch gap-3">
                    <div
                      className="text-white fw-bold small text-uppercase text-nowrap me-2 d-flex align-items-center"
                      style={{ width: 130 }}
                    >
                      Contacts
                    </div>
                    <div className="flex-grow-1">
                      <Input
                        id="ticket-contacts"
                        isMulti
                        placeholder="Select contacts"
                        value={filterContacts}
                        onChange={(v) => setFilterContacts(v)}
                        isValidNewOption={() => false}
                        loadOptions={loadContactOptionsPaginated}
                        components={{ Option: contactOption }}
                      />
                    </div>
                  </div>
                  <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 d-flex flex-wrap align-items-stretch gap-3">
                    <div
                      className="text-white fw-bold small text-uppercase text-nowrap me-2 d-flex align-items-center"
                      style={{ width: 130 }}
                    >
                      Status
                    </div>
                    <div className="flex-grow-1">
                      <Input
                        id="ticket-statuses"
                        inputType="select"
                        isMulti
                        isClearable
                        value={filterStatuses}
                        options={statuses.map((status) => ({
                          value: status,
                          label: status,
                        }))}
                        onChange={setFilterStatuses}
                      />
                    </div>
                  </div>
                </div>
              )}
              {showAdvancedFilters && (
                <div className="d-flex flex-column gap-2">
                  <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 d-flex flex-wrap align-items-stretch gap-3">
                    <div
                      className="text-white fw-bold small text-uppercase text-nowrap me-2 d-flex align-items-center"
                      style={{ width: 130 }}
                    >
                      Created
                    </div>
                    <div className="d-flex flex-row flex-wrap align-items-stretch gap-3 flex-grow-1">
                      <div style={{ minWidth: 220, flex: "1 1 240px" }}>
                        <label
                          htmlFor="ticket-date-after"
                          className="visually-hidden"
                        >
                          Created after
                        </label>
                        <Input
                          id="ticket-date-after"
                          inputType="dateTime"
                          dateTimeType="dateTime"
                          placeholder="Created after"
                          isClearable
                          value={[dateGTE]}
                          onChange={(v) => setDateGTE(v[0])}
                        />
                      </div>
                      <div style={{ minWidth: 220, flex: "1 1 240px" }}>
                        <label
                          htmlFor="ticket-date-before"
                          className="visually-hidden"
                        >
                          Created before
                        </label>
                        <Input
                          id="ticket-date-before"
                          inputType="dateTime"
                          dateTimeType="dateTime"
                          placeholder="Created before"
                          isClearable
                          value={[dateLTE]}
                          onChange={(v) => setDateLTE(v[0])}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="px-3 py-2 rounded-3 border border-light border-opacity-25 d-flex flex-wrap align-items-stretch gap-3">
                    <div
                      className="text-white fw-bold small text-uppercase text-nowrap me-2 d-flex align-items-center"
                      style={{ width: 130 }}
                    >
                      Closed
                    </div>
                    <div className="d-flex flex-row flex-wrap align-items-stretch gap-3 flex-grow-1">
                      <div style={{ minWidth: 220, flex: "1 1 240px" }}>
                        <label
                          htmlFor="ticket-closed-after"
                          className="visually-hidden"
                        >
                          Closed after
                        </label>
                        <Input
                          id="ticket-closed-after"
                          inputType="dateTime"
                          dateTimeType="dateTime"
                          placeholder="Closed after"
                          isClearable
                          value={[closedGTE]}
                          onChange={(v) => setClosedGTE(v[0])}
                        />
                      </div>
                      <div style={{ minWidth: 220, flex: "1 1 240px" }}>
                        <label
                          htmlFor="ticket-closed-before"
                          className="visually-hidden"
                        >
                          Closed before
                        </label>
                        <Input
                          id="ticket-closed-before"
                          inputType="dateTime"
                          dateTimeType="dateTime"
                          placeholder="Closed before"
                          isClearable
                          value={[closedLTE]}
                          onChange={(v) => setClosedLTE(v[0])}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="d-flex justify-content-end pt-1">
                <button
                  type="button"
                  className="btn btn-outline-light btn-sm px-3"
                  onClick={() =>
                    setShowAdvancedFilters((expanded) => !expanded)
                  }
                >
                  {showAdvancedFilters ? "Less filters" : "More filters"}
                </button>
              </div>
            </div>
          </div>
        </fieldset>
        <div className="mb-5 bg-white rounded-3 p-3">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th className="p-3">Ticket</th>
                  <th className="p-3">Description</th>
                  <th className="p-3">Topic</th>
                  <th className="p-3">Created at</th>
                  <th className="p-3 text-center">Contacts</th>
                  <th className="p-3">
                    Status
                    <button
                      type="button"
                      className="btn btn-outline-secondary border-0 btn-sm float-end"
                      onClick={() => getTickets()}
                      disabled={loading}
                      aria-label="Refresh tickets"
                      title="Refresh tickets"
                    >
                      <FontAwesomeIcon icon={faRotate} spin={loading} />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((e) => (
                  <tr key={e.uuid}>
                    <td className="p-3">{e.uuid}</td>
                    <td className="p-3">{e.description}</td>
                    <td className="p-3">{e.topic.label}</td>
                    <td className="p-3">{formatDateTime(e.createdAt)}</td>
                    <td className="p-3 text-center">
                      {e.meta.contactsCount ? (
                        <ReportingContactsList
                          label={e.meta.contactsCount}
                          uuid={e.uuid}
                          kind="ticket"
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="p-3">
                      <div className="d-flex align-items-start justify-content-between gap-2">
                        <div>
                          <div>{e.status}</div>
                          <small className="text-secondary">
                            {e.statusChanges?.data?.[0]?.changedAt
                              ? formatDateTime(
                                  e.statusChanges.data[0].changedAt
                                )
                              : "No status change recorded"}
                          </small>
                        </div>
                        <TicketStatusHistoryModal ticketUuid={e.uuid} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <MyPagination
        total={total}
        onPaginate={getTickets}
        key={[
          filterTopics,
          filterContacts,
          filterStatuses,
          search,
          dateGTE,
          dateLTE,
          closedGTE,
          closedLTE,
        ]}
      />
      <JsonCollapse json={tickets} />
    </div>
  );
}
